/**
 * Single source of truth for programme status — Round 30.
 *
 * Canonical statuses (PRD Section 1):
 *   • active   — "Now Enrolling" publicly; Enrol Now / Apply Now CTA shown
 *   • inactive — "Not In Session" publicly; Make Enquiry CTA shown
 *   • draft    — hidden from the public surface entirely
 *
 * EVERY public/student/admin page that needs to decide "is this programme
 * enrolling right now?" MUST import `isEnrolling()` from this module —
 * do NOT inline the check or read `enrollment_open` directly. This avoids
 * the historical drift where the landing page said "Now Enrolling" but
 * the detail page said "Make Enquiry" for the same programme.
 */

export const PROGRAMME_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DRAFT: "draft",
};

/** Returns the lower-cased status string with a safe fallback. */
export const statusOf = (p) => ((p?.status || "").toString().toLowerCase().trim());

/** True iff the programme is publicly enrolling RIGHT NOW. */
export const isEnrolling = (p) => statusOf(p) === PROGRAMME_STATUS.ACTIVE;

/** True iff the programme is publicly visible but not in session. */
export const isInactive = (p) => statusOf(p) === PROGRAMME_STATUS.INACTIVE;

/** True iff the programme is admin-only (hidden publicly). */
export const isDraft = (p) => statusOf(p) === PROGRAMME_STATUS.DRAFT;

/** True iff the programme should appear on any public surface. */
export const isPublic = (p) => {
  const s = statusOf(p);
  return s === PROGRAMME_STATUS.ACTIVE || s === PROGRAMME_STATUS.INACTIVE;
};

/** Human-readable tag for badges. */
export const statusTag = (p) => (isEnrolling(p) ? "Now Enrolling" : (isInactive(p) ? "Not In Session" : "Coming Soon"));

/** Tailwind class for the badge background colour. */
export const statusBadgeClass = (p) => (
  isEnrolling(p) ? "bg-emerald-600 text-white" : "bg-slate-400 text-white"
);
